import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import api from '../services/api';

const VerifyEmailPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [licenseKey, setLicenseKey] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent duplicate calls from React strict mode double-mounting
      if (hasVerified.current) return;
      hasVerified.current = true;

      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token found. Please check your email link.');
        return;
      }

      try {
        const response = await api.post('/Auth/verify-email', { token });

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Your email has been verified successfully!');
          setLicenseKey(response.data.licenseKey);
          setExpirationDate(response.data.expirationDate);

          // Update user data in localStorage to reflect email verification
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            user.IsEmailVerified = true;
            user.isEmailVerified = true; // Also set camelCase version for consistency
            localStorage.setItem('user', JSON.stringify(user));

            // Dispatch custom event to notify other components
            window.dispatchEvent(new Event('userVerificationChanged'));
          }
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Verification failed');
        }
      } catch (err) {
        setStatus('error');
        if (err.response?.data?.message) {
          setMessage(err.response.data.message);
        } else {
          setMessage('An error occurred during verification. The link may have expired.');
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-gray-100 dark:bg-gray-900`}>
      <div className="px-4 sm:px-5 lg:px-7 flex flex-grow items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-md mt-6">
          {status === 'verifying' && (
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">Verifying your email...</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Please wait a moment</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <svg className="mx-auto h-16 w-16 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">Email Verified!</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</p>

              {licenseKey && (
                <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                  <div className="flex items-center justify-center mb-3">
                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Your Trial License</h3>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-md p-4 mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">License Key</p>
                    <p className="text-lg font-mono font-bold text-gray-900 dark:text-white break-all">{licenseKey}</p>
                  </div>
                  {expirationDate && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Valid until: <span className="font-semibold">{new Date(expirationDate).toLocaleDateString()}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
                    This license key has also been sent to your email.
                  </p>
                </div>
              )}

              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/downloads')}
                  className="block w-full text-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Download Stonewall Invoicing
                </button>
                <button
                  onClick={() => navigate('/account/profile')}
                  className="block w-full text-center font-medium text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Go to Your Account
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <svg className="mx-auto h-16 w-16 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">Verification Failed</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</p>
              <div className="mt-6 space-y-3">
                <Link
                  to="/account/settings"
                  className="block w-full text-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Resend Verification Email
                </Link>
                <Link
                  to="/"
                  className="block text-center font-medium text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
